/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2021 ICEMEDX, Inc.
***********************************************/
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Validators, FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material';

import {
  IcecontrolSettings,
  iceColumnDefinition,
  IcemattableSettings,
  IcepanelgroupSettings,
  IcepanelsectionSettings,
  IceexpansionpanelSettings,
  IcepanelcontainerRowSettings,
  IcepanelcontainerSettings,
  IcecontrolPermission,
  SECTION_CONTROL_ALIGNMENT,
  IcepanelSettings
} from 'projects/icemedx-lib/src/components/models/icesettings';

import { IceEntity } from 'projects/icemedx-lib/src/models/core/IceEntity';
import { provider } from 'projects/icemedx-lib/src/models/domain/provider';

import { AppService } from '../app.service';
import { IceNavService } from '../../../../icemedx-lib/src/components/ice-nav/ice-nav.service';
import { LoginService } from '../../../../icemedx-lib/src/login/login.service';
import { DomainService } from 'projects/icemedx-lib/src/services/domain/domain.service';
import { VendorService } from '../../../../icemedx-lib/src/services/vendor/vendor.service';
import { ProviderService } from '../../../../icemedx-lib/src/services/provider/provider.service';
import { IceTypes } from 'projects/icemedx-lib/src/models/icetypes';
import { userTypes } from 'projects/icemedx-lib/src/login/login.config';

import { DomainProfile } from '../domainprofile';
import { MedicalcodesService } from '../../../../icemedx-lib/src/services/medicalcodes/medicalcodes.service';
import { pharmacy } from '../../../../icemedx-lib/src/models/domain/pharmacy';
import { PharmacyService } from '../../../../icemedx-lib/src/services/pharmacy/pharmacy.service';
import { PayerService } from '../../../../icemedx-lib/src/services/payer/payer.service';
import { basecode } from '../../../../icemedx-lib/src/models/ehr/subs/basecode';
import { Iceutility } from '../../../../icemedx-lib/src/services/iceutility';
import { OrderService } from '../../../../icemedx-lib/src/services/orders/order.service';
import { MessageService } from '../../../../icemedx-lib/src/services/message/message.service';

@Component({
  selector: 'app-pharmacy',
  templateUrl: './pharmacy.component.html',
  styleUrls: ['./pharmacy.component.scss'],
  providers: [AppService, IceNavService]
})
export class PharmacyComponent extends DomainProfile implements OnInit {
  // Data Members
  pharmacyObj: pharmacy = null;

  profileViewSettings: IcepanelcontainerSettings = null;
  btnSaveProfile: IcecontrolSettings = new IcecontrolSettings("SaveButton", { text: "Save Profile", class: [], glyphClass: "fa-plus-circle" }, null, null);
  btnCloudDownload: IcecontrolSettings = new IcecontrolSettings("DownloadButton", { text: "Download Pharmacy Information", class: [], glyphClass: "fa-cloud-download-alt" }, null, null).setDisabled(true);

  constructor(appSvc: AppService,
    domainSvc: DomainService,
    vendorSvc: VendorService,
    proSvc: ProviderService,
    pharmSvc: PharmacyService,
    payerSvc: PayerService, 
    medCodeSvc: MedicalcodesService,
    messageSvc: MessageService,
    loginSvc: LoginService,
    navSvc: IceNavService,
    router: Router) {
    super(appSvc, domainSvc, vendorSvc, proSvc, pharmSvc, payerSvc, medCodeSvc, messageSvc, loginSvc, navSvc, router);

    appSvc.defaultMenus();
    appSvc.addBreadcrumb("Pharmacy Profile", {}, true, "");

    domainSvc.getPharmacy(appSvc.getCurrentAccount()).subscribe(
      pro => {
        this.profileViewSettings = this.viewSetup(pro);

        if (this.getProviderHIEUpdateDisable()) {
          this.btnCloudDownload.setDisabled(false);
        }
      },
      error => {
      });
  }

  ngOnInit() {
    this.getAppService().refreshMenus();
  } 

  viewSetup(dat: IceEntity): IcepanelcontainerSettings {
    let setup: IcepanelcontainerSettings = null;
    try {
      let feaAccess = this.getAppService().getPermission(userTypes.USER_FEATURES.ufSetup).default_access;
      this.btnSaveProfile.permissions = new IcecontrolPermission(userTypes.USER_FEATURES.ufSetup, feaAccess == userTypes.USER_FEATURE_ACCESS.ufaNone ? userTypes.USER_FEATURE_ACCESS.ufaReadOnly : feaAccess);

      if (feaAccess == userTypes.USER_FEATURE_ACCESS.ufaNone) {
        this.getDomainService().logMessage(IceTypes.LOGTYPE.ltError, "You do not have access to view this data.");
        return null;
      }
      if (dat == null || dat["length"] == 0) {
        return null;
      }

      this.pharmacyObj = <pharmacy>dat[0];

      //////////////////////
      // Name
      if (this.pharmacyObj.COUNTRIES == null || this.pharmacyObj.COUNTRIES.length == 0) {
        this.pharmacyObj.COUNTRIES = [new basecode("COUNTRY")];
      }
      if (this.pharmacyObj.CURRENCIES == null || this.pharmacyObj.CURRENCIES.length == 0) {
        this.pharmacyObj.CURRENCIES = [new basecode("CURRENCY")];
      }

      let cntryCode: basecode = this.pharmacyObj.COUNTRIES[0];
      let curcyCode: basecode = this.pharmacyObj.CURRENCIES[0];
      let nameControls = super.viewName(this.pharmacyObj.trans_account, this.pharmacyObj.ENTS, this.pharmacyObj.entity_key, true, this.pharmacyObj.taxonomy_code, cntryCode, curcyCode);
      let nameSections: IcepanelsectionSettings[] = [
        new IcepanelsectionSettings("NameViewSection", { class: ["yalla"], styles: { width: "100%" } }, null, nameControls, null)
      ];

      //////////////////////
      // Contact Info
      let contactTables = super.viewContact(this.pharmacyObj.CONS);
      let contactSections = this.viewContactSection(contactTables, true);

      //////////////////////
      // Address Info
      let addressTables = this.viewAddress(this.pharmacyObj.ADS);
      let addressSections = this.viewAddressSection(addressTables, true);

      //////////////////////
      // Identification Info
      let idCodeList = [
        "PI", "XV", "ZZ", "24", "46", "AC", "D3", "STOREID", "FILEID", "0B", "1C", "1D",
        "PPO", "2U", "BO", "DH", "HIN", "SECCOVID", "NF", "PROMOID", "SY", "G1", "ORGID"
      ];
      let idTables = super.viewIdentification(this.pharmacyObj.IDS, idCodeList);
      let idSections = this.viewIdentificationSection(idTables, idCodeList, true);

      /////////////////////////
      // Network Connections
      let networkConnSections = this.viewNewOutboundNetworkConnections();
      let outboundNetworkConnectionTables = this.viewOutboundNetworkConnections(this.pharmacyObj.CONNPARAMS, this.pharmacyObj.TYPEREFS);
      let outboundGroups: IcepanelgroupSettings[] = [
        new IcepanelgroupSettings(null, outboundNetworkConnectionTables)
      ];

      networkConnSections.push(new IcepanelsectionSettings("OutboundNetworkConnectionViewSection", { class: ["yalla"], styles: { width: "100%" } }, outboundGroups, [], null));
      networkConnSections = networkConnSections.concat(this.viewEditOutboundNetworkConnections());

      let inboundNetworkConnectionTables = this.viewInboundNetworkConnections(this.pharmacyObj.INCONNPARAMS, this.pharmacyObj.TYPES);
      let inboundGroups: IcepanelgroupSettings[] = [
        new IcepanelgroupSettings(null, inboundNetworkConnectionTables)
      ];
      networkConnSections.push(new IcepanelsectionSettings("InboundNetworkConnectionViewSection", { class: ["yalla"], styles: { width: "100%" } }, inboundGroups, [], null));
      networkConnSections = networkConnSections.concat(this.viewEditInboundNetworkConnections());

      let networkInfoTables = this.viewNetworkInformation(<IceEntity>this.pharmacyObj, this.pharmacyObj.SVCLEVELS);
      let networkInfoGroups: IcepanelgroupSettings[] = [
        new IcepanelgroupSettings(null, networkInfoTables)
      ];
      networkConnSections.push(new IcepanelsectionSettings("NetworkInformationViewSection", { class: ["yalla"], styles: { width: "100%" } }, networkInfoGroups, [], null));

      let namefrm: IcepanelSettings = new IceexpansionpanelSettings("NameView", "", "Name", "", nameSections, null, null, null)
        .enableForm();
      let nameFrmGrp = namefrm.getForm();
      let panels: IcepanelSettings[] = [
        namefrm,
        new IceexpansionpanelSettings("ContactView", "", "Contact Information", "", contactSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("AddressView", "", "Address Information", "", addressSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("IdentificationView", "", "Identification Information", "", idSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("NetworkConnectionView", "", "Network Connections", "", networkConnSections, null, null, null)
          .enableForm(nameFrmGrp),
      ];

      let panelrows: IcepanelcontainerRowSettings[] = [new IcepanelcontainerRowSettings(panels)];
      setup = new IcepanelcontainerSettings("ContactViewContainer", panelrows);
    }
    catch (e) {
      console.log(e);
    }
    return setup;
  }

  // Events
  onControlChange(event) {
    debugger;
    super.onControlChange(event);
  }

  onButtonClick(event) {
    debugger;
    this.onDefaultButtonClick(event, this.pharmacyObj.CONNPARAMS, this.pharmacyObj.TYPEREFS, this.editInboundRow);
  }

  public onRowClick(event) {
    debugger;
    super.onRowClick(event);
  }

  public onAddRowButtonClick(event) {
    this.onDefaultAddRowButtonClick(event,
      this.pharmacyObj.CONS,
      this.pharmacyObj.ADS,
      this.pharmacyObj.IDS,
      this.pharmacyObj.CONNPARAMS,
      this.pharmacyObj.TYPEREFS,
      this.pharmacyObj.INCONNPARAMS,
      this.pharmacyObj.TYPES);
  }

  onSaveProfile(event) {
    let taxonomyCtrl = this.getTaxonomyCodeControl();
    if (taxonomyCtrl != null) {
      let txFrmCtrl = taxonomyCtrl.getFormControl();
      if (txFrmCtrl && txFrmCtrl.dirty) {
        this.pharmacyObj.taxonomy_code = txFrmCtrl.value;
      }
    }
    if (this.pharmacyObj.taxonomy_code.length == 0) {
      this.getDomainService().logMessage(IceTypes.LOGTYPE.ltError, "The Taxonomy code field is required.");
      return;
    }

    if (false == this.updateName(this.pharmacyObj.COUNTRIES, this.pharmacyObj.CURRENCIES)) {
      // To do error log
      return;
    }

    this.updateContact(this.pharmacyObj.CONS);
    this.updateAddress(this.pharmacyObj.ADS);
    this.updateIdentification(this.pharmacyObj.IDS);
    this.updateOutboundDomainConnection(this.pharmacyObj.CONNPARAMS, this.pharmacyObj.TYPEREFS);
    this.updateInboundDomainConnection(this.pharmacyObj.INCONNPARAMS, this.pharmacyObj.TYPES);
    this.getPharmacyService().updatePharmacy(this.pharmacyObj).subscribe(
      data => {
        this.getRouter().navigate([this.getNavService().buildRoute("dashboard", false)]);
      },
      error => {
        console.log(error);
    });
  }

  onCloudDownload(event) {
    this.getPharmacyService().getHIEOrganization(this.getAppService().getCurrentAccount(), this.getAppService().getCurrentAccountType()).subscribe(
      data => {
        // Set loader hide
      },
      error => {
        // Set loader hide
        console.log(error);
      });
  }
}
